import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";

interface SubscriptionContextType {
  subscribed: boolean;
  subscriptionTier: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
  checkSubscription: () => Promise<void>;
  getPropertyLimit: () => number;
  propertyCount: number;
  setPropertyCount: (count: number) => void;
  // Trial functionality - DISPLAY ONLY, does not grant access
  isTrialActive: boolean;
  trialPlan: string | null;
  trialEndsAt: string | null;
  trialDaysRemaining: number;
  isTrialExpired: boolean;
  // Enhanced feature access methods
  getTenantLimit: () => number;
  hasLeaseSigningAccess: () => boolean;
  isEnterprise: boolean;
  isStarter: boolean;
  isFree: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile } = useAuth();
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [propertyCount, setPropertyCount] = useState(0);

  // Trial state - DISPLAY ONLY, does not grant access
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [trialPlan, setTrialPlan] = useState<string | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [isTrialExpired, setIsTrialExpired] = useState(false);

  const checkSubscription = useCallback(async () => {
    if (!user) {
      console.log("[SubscriptionContext] No user, resetting subscription state");
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log("[SubscriptionContext] ========== SUBSCRIPTION CHECK START ==========");
      console.log("[SubscriptionContext] User ID:", user.id);
      console.log("[SubscriptionContext] User Email:", user.email);

      // Clear any stale cache before fetching
      localStorage.removeItem("subscription_cache");
      sessionStorage.removeItem("subscription_cache");
      console.log("[SubscriptionContext] Cache cleared");

      // Fetch from RPC - no caching, always fresh
      const { data, error } = await supabase.rpc("get_subscription_status");

      if (error) {
        console.error("[SubscriptionContext] ❌ RPC ERROR:", error);
        console.error("[SubscriptionContext] Error details:", JSON.stringify(error, null, 2));
        setSubscribed(false);
        setSubscriptionTier(null);
        setSubscriptionEnd(null);
        return;
      }

      // Detailed logging of raw response
      console.log("[SubscriptionContext] ✅ RAW RPC Response:", JSON.stringify(data, null, 2));

      // Handle TABLE response (returns array) or JSON response (object) for backwards compatibility
      const subscriptionData = Array.isArray(data) ? data[0] : data;

      if (!subscriptionData) {
        console.log("[SubscriptionContext] ⚠️ No subscription data returned from RPC");
        setSubscribed(false);
        setSubscriptionTier(null);
        setSubscriptionEnd(null);
        setIsTrialActive(false);
        setTrialPlan(null);
        setTrialEndsAt(null);
        return;
      }

      // Log each field individually with types
      console.log(
        "[SubscriptionContext] Field: subscribed =",
        subscriptionData.subscribed,
        "(type:",
        typeof subscriptionData.subscribed,
        ")",
      );
      console.log(
        "[SubscriptionContext] Field: subscription_tier =",
        subscriptionData.subscription_tier,
        "(type:",
        typeof subscriptionData.subscription_tier,
        ")",
      );
      console.log(
        "[SubscriptionContext] Field: subscription_end =",
        subscriptionData.subscription_end,
        "(type:",
        typeof subscriptionData.subscription_end,
        ")",
      );

      // STRICT: Only grant access if there's a verified active subscription
      const hasActiveSubscription =
        subscriptionData.subscribed === true &&
        subscriptionData.subscription_tier &&
        subscriptionData.subscription_end &&
        new Date(subscriptionData.subscription_end) > new Date();

      console.log("[SubscriptionContext] ========== VALIDATION RESULTS ==========");
      console.log("[SubscriptionContext] subscribed === true?", subscriptionData.subscribed === true);
      console.log("[SubscriptionContext] has subscription_tier?", !!subscriptionData.subscription_tier);
      console.log("[SubscriptionContext] has subscription_end?", !!subscriptionData.subscription_end);
      if (subscriptionData.subscription_end) {
        console.log(
          "[SubscriptionContext] subscription_end > now?",
          new Date(subscriptionData.subscription_end) > new Date(),
        );
        console.log("[SubscriptionContext] End date:", new Date(subscriptionData.subscription_end).toISOString());
        console.log("[SubscriptionContext] Current date:", new Date().toISOString());
      }
      console.log("[SubscriptionContext] FINAL: hasActiveSubscription =", hasActiveSubscription);
      console.log("[SubscriptionContext] ==========================================");

      setSubscribed(hasActiveSubscription);
      setSubscriptionTier(hasActiveSubscription ? subscriptionData.subscription_tier : null);
      setSubscriptionEnd(hasActiveSubscription ? subscriptionData.subscription_end : null);

      // Trial information - DISPLAY ONLY, does not grant access
      if (subscriptionData.is_trial_active && subscriptionData.trial_plan && subscriptionData.trial_ends_at) {
        const trialEndDate = new Date(subscriptionData.trial_ends_at);
        const now = new Date();
        const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        console.log("[SubscriptionContext] Trial active:", daysRemaining > 0, "days remaining:", daysRemaining);

        setIsTrialActive(daysRemaining > 0);
        setTrialPlan(subscriptionData.trial_plan);
        setTrialEndsAt(subscriptionData.trial_ends_at);
        setTrialDaysRemaining(Math.max(0, daysRemaining));
        setIsTrialExpired(daysRemaining <= 0);
      } else {
        setIsTrialActive(false);
        setTrialPlan(null);
        setTrialEndsAt(null);
        setTrialDaysRemaining(0);
        setIsTrialExpired(false);
      }
    } catch (error) {
      console.error("[SubscriptionContext] Error checking subscription:", error);
      // On error, assume no subscription access
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const getPropertyLimit = useCallback((): number => {
    // STRICT: ONLY grant access based on ACTIVE Stripe subscriptions
    // Trial information is for display only - NO ACCESS GRANTED
    if (subscribed && subscriptionTier) {
      switch (subscriptionTier) {
        case "Starter":
        case "Premium":
          return 15; // Match database trigger limit
        case "Enterprise":
          return Infinity; // Unlimited
        default:
          // No fallback - unknown subscription tiers get free tier only
          return 3;
      }
    }

    // Free tier only - trials do NOT grant additional access
    return 3;
  }, [subscribed, subscriptionTier]);

  useEffect(() => {
    if (user) {
      checkSubscription();
    } else {
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
      setLoading(false);
    }
  }, [user, checkSubscription]);

  const getTenantLimit = useCallback((): number => {
    // STRICT: ONLY grant access based on ACTIVE Stripe subscriptions
    if (subscribed && subscriptionTier) {
      switch (subscriptionTier) {
        case "Starter":
          return 5;
        case "Premium":
        case "Enterprise":
          return Infinity; // Unlimited
        default:
          return 3;
      }
    }

    // Free tier only - trials do NOT grant additional access
    return 3;
  }, [subscribed, subscriptionTier]);

  const hasLeaseSigningAccess = useCallback((): boolean => {
    // STRICT: Only Starter and above have lease signing access
    return subscribed && subscriptionTier !== null && ["Starter", "Premium", "Enterprise"].includes(subscriptionTier);
  }, [subscribed, subscriptionTier]);

  // Tier flags for easy access
  const isEnterprise = subscribed && subscriptionTier === "Enterprise";
  // isStarter means "has Starter tier or above" (Starter, Premium, or Enterprise)
  const isStarter = subscribed && ["Starter", "Premium", "Enterprise"].includes(subscriptionTier || "");
  const isFree = !subscribed || !subscriptionTier;

  const value = useMemo(() => ({
    subscribed,
    subscriptionTier,
    subscriptionEnd,
    loading,
    checkSubscription,
    getPropertyLimit,
    propertyCount,
    setPropertyCount,
    isTrialActive,
    trialPlan,
    trialEndsAt,
    trialDaysRemaining,
    isTrialExpired,
    getTenantLimit,
    hasLeaseSigningAccess,
    isEnterprise,
    isStarter,
    isFree,
  }), [
    subscribed,
    subscriptionTier,
    subscriptionEnd,
    loading,
    checkSubscription,
    getPropertyLimit,
    propertyCount,
    isTrialActive,
    trialPlan,
    trialEndsAt,
    trialDaysRemaining,
    isTrialExpired,
    getTenantLimit,
    hasLeaseSigningAccess,
    isEnterprise,
    isStarter,
    isFree,
  ]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};
